/* Shared */
import { Route, SessionHelper } from 'application/Shared/Helpers';

/* Hooks */
import { useSession } from 'hooks/redux/Selectors/session';

/*  React  */
import React, { createContext, useContext, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

interface AdminUsersTableContextProviderProps {
    children: React.ReactNode;
}

export interface AdminUsersTableContextProps {
    baseRoute: string;
    disabling: string | null;
    enabling: string | null;
    isSessionANM: boolean;
    navigate: (path: string) => void;
    openMergeModal: boolean;
    openSendEmail: string | null;
    setDisabling: React.Dispatch<React.SetStateAction<string | null>>;
    setEnabling: React.Dispatch<React.SetStateAction<string | null>>;
    setOpenMergeModal: React.Dispatch<React.SetStateAction<boolean>>;
    setOpenSendEmail: React.Dispatch<React.SetStateAction<string | null>>;
}

export const AdminUsersTableContext = createContext<
    AdminUsersTableContextProps | undefined
>(undefined);

/**
 * Alows to share users data state across user dashboard panels.
 * @param {React.ReactNode} children - The children components to be wrapped by the context provider.
 * @returns {JSX.Element} - The context provider component.
 * */
export const AdminUsersTableContextProvider = ({
    children
}: AdminUsersTableContextProviderProps) => {
    const navigate = useNavigate();
    const { clientId, partnerId } = useParams();
    if (!clientId) {
        throw new Error('`clientId` is required in the URL params');
    }
    const baseRoute = Route.buildBaseAdminRoute({ clientId, partnerId });
    const session = useSession();
    const sessionHelper = new SessionHelper(session);
    const isSessionANM = sessionHelper.isANM();

    const [disabling, setDisabling] = useState<string | null>(null);
    const [enabling, setEnabling] = useState<string | null>(null);
    const [openMergeModal, setOpenMergeModal] = useState(false);
    const [openSendEmail, setOpenSendEmail] = useState<string | null>(null);

    const providerValues = useMemo<AdminUsersTableContextProps>(
        () => ({
            baseRoute,
            disabling,
            enabling,
            isSessionANM,
            navigate,
            openMergeModal,
            openSendEmail,
            setDisabling,
            setEnabling,
            setOpenMergeModal,
            setOpenSendEmail
        }),
        [
            baseRoute,
            disabling,
            enabling,
            navigate,
            isSessionANM,
            openMergeModal,
            openSendEmail
        ]
    );

    return (
        <AdminUsersTableContext.Provider value={providerValues}>
            {children}
        </AdminUsersTableContext.Provider>
    );
};

/**
 * Custom hook to access the AdminUsersTable context.
 * This hook must be used within an AdminUsersTableContextProvider component.
 *
 * @returns {AdminUsersTableContextProps} The context values for AdminUsersTable
 *
 * @throws {Error} If used outside of AdminUsersTableContextProvider
 */
export const useAdminUsersTableContext = () => {
    const context = useContext(AdminUsersTableContext);

    if (context === undefined) {
        throw new Error(
            '`useAdminUsersTableContext` must be used within `AdminUsersTableContextProvider`'
        );
    }

    return context;
};
